import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle } from 'lucide-react';
import { api } from '../../../shared/api/api.js';
import type { Book } from '../../../shared/types/index.js';
import { useAuthStore } from '../../auth/store/useAuthStore.js';
import { useBodyScrollLock } from '../../../shared/utils/scrollLock.js';
import {
  BulkImportHeader,
  BulkImportDropzone,
  BulkImportPreviewTable,
  BulkImportSuccessView,
  BulkImportFooter,
  parseCsvLine,
  type ParsedRow,
} from './bulk-import/index.js';

interface BulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const BulkImportModal: React.FC<BulkImportModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuthStore();
  const [dragActive, setDragActive] = useState(false);
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successResult, setSuccessResult] = useState<{ count: number; books: Book[] } | null>(null);

  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  const processCsvFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (!text) return;

      const lines = text.split(/\r?\n/);
      if (lines.length < 2) {
        setErrorMsg('File CSV kosong atau tidak memiliki data baris.');
        return;
      }

      // Clean header and detect delimiter
      const headerLine = lines[0];
      const delimiter = (headerLine.includes(';') && (headerLine.match(/;/g) || []).length > (headerLine.match(/,/g) || []).length) ? ';' : ',';
      const parsedHeader = parseCsvLine(headerLine, delimiter).map((h) =>
        h.toLowerCase().trim().replace(/^\uFEFF/, '').replace(/^ï»¿/, '')
      );

      // Map column positions based on header names or positional fallback
      const findColIdx = (keywords: string[], fallbackIdx: number): number => {
        const found = parsedHeader.findIndex((h) =>
          keywords.some((kw) => h.includes(kw))
        );
        return found !== -1 ? found : fallbackIdx;
      };

      const titleIdx = findColIdx(['judul buku', 'judul', 'title'], 0);
      const subtitleIdx = findColIdx(['subjudul', 'sub judul', 'subtitle'], 1);
      const originalTitleIdx = findColIdx(['judul asli', 'original title', 'asli'], 2);
      const authorIdx = findColIdx(['penulis', 'author'], 3);
      const authorWhatsappIdx = findColIdx(['whatsapp', 'wa penulis', 'telepon penulis', 'phone'], 4);
      const publisherIdx = findColIdx(['penerbit', 'publisher'], 5);
      const categoryIdx = findColIdx(['kategori', 'category'], 6);
      const yearIdx = findColIdx(['tahun terbit', 'tahun', 'year'], 7);
      const editionIdx = findColIdx(['edisi', 'edition'], 8);
      const editionTypeIdx = findColIdx(['tipe edisi', 'jenis edisi', 'edition type'], 9);
      const editionNotesIdx = findColIdx(['catatan edisi', 'edition notes'], 10);
      const pagesIdx = findColIdx(['halaman', 'pages', 'page'], 11);
      const languageIdx = findColIdx(['bahasa', 'language'], 12);
      const isbnOriginalIdx = findColIdx(['isbn orisinal', 'isbn asli', 'isbn'], 13);
      const synopsisIdx = findColIdx(['sinopsis', 'synopsis', 'deskripsi'], 14);
      const tocIdx = findColIdx(['daftar isi', 'table of contents', 'toc'], 15);
      const coverUrlIdx = findColIdx(['url sampul', 'cover url', 'cover', 'sampul'], 16);
      const purchaseUrlIdx = findColIdx(['tautan pembelian', 'link pembelian', 'purchase url', 'toko'], 17);

      // Validate that at least Title, Category, or Year is recognized
      const hasCoreHeader = parsedHeader.some((ph) =>
        ['judul', 'kategori', 'tahun', 'title', 'category', 'year', 'penulis', 'penerbit'].some((k) => ph.includes(k))
      );

      if (!hasCoreHeader && parsedHeader.length < 3) {
        setErrorMsg('Format header CSV tidak valid. Pastikan Anda mengunduh template resmi dan tidak mengubah kolom utamanya.');
        return;
      }

      const rows: ParsedRow[] = [];
      setErrorMsg(null);

      // Loop over data rows
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue; // skip empty lines

        const columns = parseCsvLine(line, delimiter);
        const title = columns[titleIdx] || '';
        const subtitle = subtitleIdx < columns.length ? columns[subtitleIdx] : '';
        const originalTitle = originalTitleIdx < columns.length ? columns[originalTitleIdx] : '';
        const authorName = columns[authorIdx] || '';
        const authorWhatsapp = authorWhatsappIdx < columns.length ? columns[authorWhatsappIdx] : '';
        const publisherName = columns[publisherIdx] || '';
        const category = columns[categoryIdx] || '';
        const publicationYear = columns[yearIdx] || '';
        const edition = columns[editionIdx] || '1';
        const editionType = columns[editionTypeIdx] || 'orisinal';
        const editionNotes = columns[editionNotesIdx] || '';
        const pages = columns[pagesIdx] || '0';
        const language = columns[languageIdx] || 'Indonesia';
        const isbnOriginal = columns[isbnOriginalIdx] || '';
        const synopsis = columns[synopsisIdx] || '';
        const tableOfContents = tocIdx < columns.length ? columns[tocIdx] : '';
        const coverUrl = coverUrlIdx < columns.length ? columns[coverUrlIdx] : '';
        const purchaseUrl = purchaseUrlIdx < columns.length ? columns[purchaseUrlIdx] : '';

        const errors: string[] = [];

        // Validation Rules
        if (!title.trim() || title.trim().length < 2) {
          errors.push('Judul wajib diisi (minimal 2 karakter)');
        }
        if (!category.trim() || category.trim().length < 2) {
          errors.push('Kategori wajib diisi');
        }

        const year = parseInt(publicationYear, 10);
        if (isNaN(year) || year < 1900 || year > 2100) {
          errors.push('Tahun terbit harus angka antara 1900 - 2100');
        }

        const pageNum = parseInt(pages, 10);
        if (pages && (isNaN(pageNum) || pageNum < 0)) {
          errors.push('Halaman harus berupa angka positif');
        }

        // Role-based metadata validation
        if (user?.role === 'penulis') {
          if (!publisherName.trim()) {
            errors.push('Sebagai Penulis, kolom Penerbit wajib diisi');
          }
        } else if (user?.role === 'penerbit') {
          if (!authorName.trim()) {
            errors.push('Sebagai Penerbit, kolom Penulis wajib diisi');
          }
        } else if (user?.role === 'admin') {
          if (!authorName.trim()) {
            errors.push('Admin wajib mengisi kolom Penulis');
          }
          if (!publisherName.trim()) {
            errors.push('Admin wajib mengisi kolom Penerbit');
          }
        }

        rows.push({
          index: i,
          title,
          subtitle: subtitle || undefined,
          originalTitle: originalTitle || undefined,
          authorName: user?.role === 'penulis' ? user.name : authorName,
          authorWhatsapp: authorWhatsapp || undefined,
          publisherName: user?.role === 'penerbit' ? user.organization || user.name : publisherName,
          category,
          publicationYear,
          edition,
          editionType,
          editionNotes: editionNotes || undefined,
          pages,
          language,
          isbnOriginal: isbnOriginal || undefined,
          synopsis: synopsis || undefined,
          tableOfContents: tableOfContents || undefined,
          coverUrl: coverUrl || undefined,
          purchaseUrl: purchaseUrl || undefined,
          errors,
          isValid: errors.length === 0,
        });
      }

      setParsedRows(rows);
    };

    reader.onerror = () => {
      setErrorMsg('Gagal membaca file CSV. Pastikan file dalam keadaan baik.');
    };

    reader.readAsText(file, 'UTF-8');
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.name.endsWith('.csv')) {
        processCsvFile(file);
      } else {
        setErrorMsg('Hanya file bertipe .csv yang didukung.');
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processCsvFile(e.target.files[0]);
    }
  };

  const handleRemoveRow = (index: number) => {
    setParsedRows((prev) => prev.filter((row) => row.index !== index));
  };

  const handleImportSubmit = async () => {
    const validRows = parsedRows.filter((r) => r.isValid);
    if (validRows.length === 0) {
      setErrorMsg('Tidak ada baris data yang valid untuk diimpor.');
      return;
    }

    setIsUploading(true);
    setErrorMsg(null);

    try {
      // Format to API structure
      const payload = validRows.map((r) => ({
        title: r.title,
        subtitle: r.subtitle || undefined,
        originalTitle: r.originalTitle || undefined,
        authorName: r.authorName,
        authorWhatsapp: r.authorWhatsapp || undefined,
        publisherName: r.publisherName,
        category: r.category,
        publicationYear: parseInt(r.publicationYear, 10),
        edition: r.edition || '1',
        editionType: r.editionType || 'orisinal',
        editionNotes: r.editionNotes || undefined,
        pages: parseInt(r.pages, 10) || 0,
        language: r.language || 'Indonesia',
        isbnOriginal: r.isbnOriginal || undefined,
        synopsis: r.synopsis || undefined,
        tableOfContents: r.tableOfContents || undefined,
        coverUrl: r.coverUrl || undefined,
        purchaseUrl: r.purchaseUrl || undefined,
      }));

      const response = await api.bulkCreateBooks(payload);
      setSuccessResult({
        count: response.count,
        books: response.books,
      });
      onSuccess();
    } catch (err: any) {
      setErrorMsg(err.message || 'Gagal memproses impor massal buku.');
    } finally {
      setIsUploading(false);
    }
  };

  const totalValid = parsedRows.filter((r) => r.isValid).length;
  const totalErrors = parsedRows.filter((r) => !r.isValid).length;

  return createPortal(
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 z-50 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-slate-200/80 w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <BulkImportHeader onClose={onClose} />

        {/* Content area */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-5">
          {errorMsg && (
            <div className="p-4 rounded-2xl bg-rose-50 border border-rose-100 text-rose-800 text-xs flex items-start gap-3">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-rose-600" />
              <div className="whitespace-pre-line leading-relaxed">
                <p className="font-bold">Terjadi Kesalahan:</p>
                <p>{errorMsg}</p>
              </div>
            </div>
          )}

          {!successResult ? (
            <>
              {/* Dropzone & Template Download */}
              {parsedRows.length === 0 && (
                <BulkImportDropzone
                  dragActive={dragActive}
                  onDrag={handleDrag}
                  onDrop={handleDrop}
                  onFileChange={handleFileChange}
                />
              )}

              {/* Parsed Rows Preview Grid */}
              {parsedRows.length > 0 && (
                <BulkImportPreviewTable
                  parsedRows={parsedRows}
                  totalValid={totalValid}
                  totalErrors={totalErrors}
                  onReset={() => setParsedRows([])}
                  onRemoveRow={handleRemoveRow}
                />
              )}
            </>
          ) : (
            /* Success screen */
            <BulkImportSuccessView
              count={successResult.count}
              books={successResult.books}
              userRole={user?.role}
              onReset={() => {
                setParsedRows([]);
                setSuccessResult(null);
                setErrorMsg(null);
              }}
              onClose={onClose}
            />
          )}
        </div>

        {/* Footer */}
        {!successResult && (
          <BulkImportFooter
            hasRows={parsedRows.length > 0}
            totalValid={totalValid}
            totalErrors={totalErrors}
            isUploading={isUploading}
            onClose={onClose}
            onSubmit={handleImportSubmit}
          />
        )}
      </div>
    </div>,
    document.body
  );
};
